import { createAction, createReducer, PayloadAction } from '@reduxjs/toolkit'
import { MobilePromotionFormData, MobilePromotionFormSource } from 'src/helpers/mobilePromotionFormValidation'

type PromotionPayloadBase = MobilePromotionFormData & {
  motorbikeId: string
  portal: string
  highlights: string[]
}

export type PromotionCreatePayload = PromotionPayloadBase & {
  photoIds: string[]
  coverPhotoId: string | null
}

export type PromotionUpdatePayload = PromotionPayloadBase & {
  promotionId: number
  photoIds: string[] | null
  coverPhotoId: string | null
}

export type PromotionStatus = 'active' | 'paused'

export interface Promotion extends MobilePromotionFormSource {
  id: number
  portal: string
  status: PromotionStatus
  photo_ids: string[]
  cover_photo_id: string | null
  public_url: string | null
}

export interface MobileListingActionPayload {
  motorbikeId: string
  promotionId: number
}

export interface PromotionsReducer {
  promotions: Promotion[]
  loaded: boolean
  loading: boolean
  error: boolean
  errorMessage: string | null
  created: boolean
  updated: boolean
}

const initialState: PromotionsReducer = {
  promotions: [],
  loaded: false,
  loading: false,
  error: false,
  errorMessage: null,
  created: false,
  updated: false
}

export type CreatePromotionActionType = PayloadAction<PromotionCreatePayload>
export type UpdatePromotionActionType = PayloadAction<PromotionUpdatePayload>

export const CREATE_PROMOTION = createAction<PromotionCreatePayload>('promotions_reducer/create')
export const CREATE_PROMOTION_SUCCESS = createAction('promotions_reducer/create_success')
export const CREATE_PROMOTION_FAILURE = createAction<string | undefined>('promotions_reducer/create_failure')
export const RESET_PROMOTION = createAction('promotions_reducer/reset')

export const UPDATE_PROMOTION = createAction<PromotionUpdatePayload>('promotions_reducer/update')
export const UPDATE_PROMOTION_SUCCESS = createAction('promotions_reducer/update_success')
export const UPDATE_PROMOTION_FAILURE = createAction<string | undefined>('promotions_reducer/update_failure')

export const PAUSE_MOBILE_LISTING = createAction<MobileListingActionPayload>('promotions_reducer/pause_mobile_listing')
export const DELETE_MOBILE_LISTING = createAction<MobileListingActionPayload>(
  'promotions_reducer/delete_mobile_listing'
)
export const RESUME_MOBILE_LISTING = createAction<MobileListingActionPayload>(
  'promotions_reducer/resume_mobile_listing'
)

export const FETCH_PROMOTIONS = createAction<string>('promotions_reducer/fetch')
export const FETCH_PROMOTIONS_SUCCESS = createAction<Promotion[]>('promotions_reducer/fetch_success')
export const FETCH_PROMOTIONS_FAILURE = createAction('promotions_reducer/fetch_failure')

export const promotionsReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(FETCH_PROMOTIONS, (state) => {
      state.promotions = []
      state.loaded = false
    })
    .addCase(FETCH_PROMOTIONS_SUCCESS, (state, action) => {
      state.promotions = action.payload
      state.loaded = true
    })
    .addCase(FETCH_PROMOTIONS_FAILURE, (state) => {
      state.promotions = []
      state.loaded = true
    })
    .addCase(CREATE_PROMOTION, (state) => {
      state.loading = true
      state.error = false
      state.errorMessage = null
      state.created = false
    })
    .addCase(CREATE_PROMOTION_SUCCESS, (state) => {
      state.loading = false
      state.error = false
      state.errorMessage = null
      state.created = true
    })
    .addCase(CREATE_PROMOTION_FAILURE, (state, action) => {
      state.loading = false
      state.error = true
      state.errorMessage = action.payload ?? null
      state.created = false
    })
    .addCase(UPDATE_PROMOTION, (state) => {
      state.loading = true
      state.error = false
      state.errorMessage = null
      state.updated = false
    })
    .addCase(UPDATE_PROMOTION_SUCCESS, (state) => {
      state.loading = false
      state.error = false
      state.errorMessage = null
      state.updated = true
    })
    .addCase(UPDATE_PROMOTION_FAILURE, (state, action) => {
      state.loading = false
      state.error = true
      state.errorMessage = action.payload ?? null
      state.updated = false
    })
    .addCase(RESET_PROMOTION, () => initialState)
})
